import React, { useCallback, useState, useMemo } from 'react';
import { Paper, Typography, Collapse, Grid, Divider } from '@rbx/ui';
import { useTranslation } from '@rbx/intl';
import useSideBarContent from '@modules/ui/shared/hooks/useSideBarContent';
import useMobileMilestoneNavigationStyles from '@modules/learning/components/MobileCourseProgressBar.styles';
import {
  processMilestones,
  findSelectedMilestoneIndex,
  getMilestoneProgressInfo,
} from '@modules/learning/utils/courseUtils';
import { getLocalLearningProgress } from '@modules/learning/utils/localStorageSync';
import TreeNode from '@modules/ui/shared/interfaces/TreeNode';
import CourseProgressBar from './CourseProgressBar';
import LessonNavigationSection from './LessonNavigationSection';

export default function MobileMilestoneNavigation() {
  const { classes } = useMobileMilestoneNavigationStyles();
  const { translate } = useTranslation();
  const { treeListData, selected } = useSideBarContent();

  const [isExpanded, setIsExpanded] = useState(false);

  const milestones = processMilestones(treeListData);
  const selectedMilestoneIndex = findSelectedMilestoneIndex(milestones, selected);
  const selectedMilestone = milestones[selectedMilestoneIndex];

  const lessonStatuses = getLocalLearningProgress();
  const { currentMilestone } = useMemo(() => {
    const progressInfo = getMilestoneProgressInfo(milestones, lessonStatuses);
    return {
      currentMilestone: progressInfo.currentMilestone,
    };
  }, [milestones, lessonStatuses]);

  const currentMilestoneNumber =
    (selectedMilestone || milestones[currentMilestone])?.milestoneIndex || 1;

  const { currentLessonPath, nextLessonPath } = useMemo(() => {
    if (!milestones || !selected) return { currentLessonPath: undefined, nextLessonPath: null };

    const allLessons: TreeNode[] = [];
    milestones.forEach((milestone) => {
      if (milestone.children) {
        allLessons.push(...milestone.children);
      }
    });

    const currentLessonIndex = allLessons.findIndex((lesson) => lesson.id === selected);
    if (currentLessonIndex === -1) {
      return { currentLessonPath: undefined, nextLessonPath: null };
    }

    const currentPath = allLessons[currentLessonIndex].path || undefined;
    const nextPath =
      currentLessonIndex < allLessons.length - 1 ? allLessons[currentLessonIndex + 1].path : null;

    return { currentLessonPath: currentPath, nextLessonPath: nextPath };
  }, [milestones, selected]);

  const handleToggle = useCallback(() => {
    setIsExpanded((prev) => !prev);
  }, []);

  return (
    <Paper className={classes.container}>
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <div className={classes.clickableHeader} onClick={handleToggle}>
        <div className={classes.dragHandle}>
          <div className={classes.dragIndicator} />
        </div>
        <Grid container alignItems='center' justifyContent='space-between'>
          <Grid item>
            <Typography variant='h5'>
              {`${translate('Label.Milestone')} ${currentMilestoneNumber} ${translate(
                'Label.OutOf',
              )} ${milestones.length}`}
            </Typography>
          </Grid>
        </Grid>
      </div>

      <Collapse in={isExpanded} timeout={300}>
        <div className={classes.expandedContent}>
          <CourseProgressBar />
        </div>
        <Divider />
        <div className={classes.continueSection}>
          <LessonNavigationSection next={nextLessonPath} currentLessonPath={currentLessonPath} />
        </div>
      </Collapse>
    </Paper>
  );
}
